import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWizardStepConfig } from './SetupWizardLayout'
import { COMMUNITY_SPORT_OPTIONS } from '../../lib/communitySport'
import {
  markStepCompleted,
  markStepSkipped,
  readDraft,
  writeDraft,
} from './signupState'

/**
 * SetupWizardSize — Wizard Step 2 ("Your community").
 *
 * Captures rough community size (optional). Invite sharing lives on
 * /community/welcome and the portal roster after setup.
 *
 * Continue → persist size → mark completed → /policies.
 * Skip     → persist size if picked → mark skipped → /policies.
 */

const STEP = 'size'
const NEXT_ROUTE = '/signup/setup/policies'

const SIZES = [
  { id: 'under_200', label: 'Under 200' },
  { id: '200_500',   label: '200-500' },
  { id: '500_1000',  label: '500-1000' },
  { id: '1000_plus', label: '1000+' },
]

export default function SetupWizardSize() {
  const navigate = useNavigate()
  const initialDraft = useMemo(readDraft, [])
  const [draftWizard] = useState(() => initialDraft.setupWizard || {})
  const [size, setSize] = useState(() => draftWizard.communitySize || '')
  const [primarySport, setPrimarySport] = useState(
    () => initialDraft.primarySport || '',
  )

  const persistSize = () => {
    const wizard = readDraft().setupWizard
    writeDraft({
      primarySport,
      setupWizard: { ...wizard, communitySize: size },
    })
  }

  const handleContinue = () => {
    persistSize()
    markStepCompleted(STEP)
    navigate(NEXT_ROUTE)
  }

  const handleSkip = () => {
    persistSize()
    markStepSkipped(STEP)
    navigate(NEXT_ROUTE)
  }

  useWizardStepConfig({
    canContinue: Boolean(primarySport),
    onContinue: handleContinue,
    onSkipStep: handleSkip,
  })

  return (
    <>
      <h1
        className="font-medium text-[var(--signup-text-primary)] text-[30px] sm:text-[36px] leading-[1.1]"
        style={{ letterSpacing: '-0.01em' }}
      >
        About your community.
      </h1>

      <section className="mt-8" aria-labelledby="wizard-sport-heading">
        <h2
          id="wizard-sport-heading"
          className="text-[15px] font-medium text-[var(--signup-text-primary)]"
        >
          Primary sport
        </h2>
        <div
          className="mt-4 flex flex-wrap gap-2"
          role="radiogroup"
          aria-labelledby="wizard-sport-heading"
        >
          {COMMUNITY_SPORT_OPTIONS.map((opt) => {
            const isSelected = primarySport === opt.id
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setPrimarySport(opt.id)}
                className={[
                  'px-5 h-[44px] rounded-full text-[14px] font-medium',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)]',
                  'motion-safe:transition-colors motion-safe:duration-150',
                  isSelected
                    ? 'bg-[var(--signup-accent-subtle)] border border-[var(--signup-accent)] text-[var(--signup-accent)]'
                    : 'bg-[var(--signup-bg-elevated)] border border-[var(--signup-border-default)] text-[var(--signup-text-primary)] hover:bg-[var(--signup-bg-subtle)] hover:border-[var(--signup-border-strong)]',
                ].join(' ')}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      </section>

      <section className="mt-10" aria-labelledby="wizard-size-heading">
        <h2
          id="wizard-size-heading"
          className="text-[15px] font-medium text-[var(--signup-text-primary)]"
        >
          How many players?
        </h2>
        <div
          className="mt-4 flex flex-wrap gap-2"
          role="radiogroup"
          aria-labelledby="wizard-size-heading"
        >
          {SIZES.map((opt) => {
            const isSelected = size === opt.id
            return (
              <button
                key={opt.id}
                type="button"
                role="radio"
                aria-checked={isSelected}
                onClick={() => setSize(isSelected ? '' : opt.id)}
                className={[
                  'px-5 h-[44px] rounded-full text-[14px] font-medium',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--signup-accent)]',
                  'motion-safe:transition-colors motion-safe:duration-150',
                  isSelected
                    ? 'bg-[var(--signup-accent-subtle)] border border-[var(--signup-accent)] text-[var(--signup-accent)]'
                    : 'bg-[var(--signup-bg-elevated)] border border-[var(--signup-border-default)] text-[var(--signup-text-primary)] hover:bg-[var(--signup-bg-subtle)] hover:border-[var(--signup-border-strong)]',
                ].join(' ')}
              >
                {opt.label}
              </button>
            )
          })}
        </div>
      </section>
    </>
  )
}
